import ConsumptionReturnDrawer from './components/ConsumptionReturnDrawer';
import ConsumptionReturnsPanel from './components/ConsumptionReturnsPanel';
import { CONSUMPTION_TABS } from './constants/consumptionsConstants';
import { RETURN_TEXTS } from './constants/consumptionReturnTexts';
import { useConsumptionReturnsPanel } from './hooks/useConsumptionReturnsPanel';
import { usePermissions } from '../../hooks/usePermissions';
import { useState } from 'react';
import { Alert, Box, Snackbar, Tabs, Tab } from '@mui/material';
import { FormTitleBox } from '../../common/components';
import { usePrintConsumptionDelivery } from '../../hooks/useConsumptions';
import { getConsumptionApiErrorMessage } from './utils/consumptionsUtils';
import ConsumptionsDataTable from './components/ConsumptionsDataTable';
import ConsumptionsFilters from './components/ConsumptionsFilters';
import ConsumptionsPagination from './components/ConsumptionsPagination';
import ConsumptionDetailDialog from './components/ConsumptionDetailDialog';
import { useConsumptionsTable } from './hooks/useConsumptionsTable';

const ConsumptionsTable = () => {
  const [activeTab, setActiveTab] = useState(CONSUMPTION_TABS.CONSUMPTIONS);
  const { permissions } = usePermissions();
  const [detailActionPending, setDetailActionPending] = useState(false);
  const [printFeedback, setPrintFeedback] = useState({ open: false, message: '', severity: 'info' });
  const printMutation = usePrintConsumptionDelivery();
  const {
    scopeOptions,
    data,
    isLoading,
    totalPages,
    totalCount,
    page,
    rowsPerPage,
    searchTerm,
    filters,
    warehouses,
    detailDialogOpen,
    selectedConsumptionId,
    handleSearch,
    handleFilterChange,
    handlePageChange,
    handleRowsPerPageChange,
    handleViewDetail,
    handleCloseDetailDialog,
    handleRefreshList,
    sort,
    handleSort,
    resetSort,
  } = useConsumptionsTable();
  const {
    drawerProps: returnDrawerProps,
    resetSort: resetReturnsSort,
    ...returnsPanelProps
  } = useConsumptionReturnsPanel({ enabled: activeTab === CONSUMPTION_TABS.RETURNS });

  const handleTabChange = (_, value) => {
    setActiveTab(value);
    resetSort();
    resetReturnsSort();
  };

  const handlePrintConsumption = async (consumption) => {
    if (!consumption?.uuid || printMutation.isPending) return;

    try {
      await printMutation.mutateAsync(consumption.uuid);
      setPrintFeedback({
        open: true,
        severity: 'success',
        message: 'Comprobante PDF descargado correctamente.',
      });
    } catch (error) {
      setPrintFeedback({
        open: true,
        severity: 'error',
        message: getConsumptionApiErrorMessage(error, 'No se pudo generar el comprobante PDF'),
      });
    }
  };

  return (
    <Box>
      <FormTitleBox
        title="Stock y compras | Consumos de stock"
        showSearch={activeTab === CONSUMPTION_TABS.CONSUMPTIONS}
        searchLabel="Buscar consumo"
        searchValue={searchTerm}
        onSearchChange={handleSearch}
        searchSize="medium"
        isLoading={detailActionPending || printMutation.isPending}
      />

      <Box sx={{ backgroundColor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ minHeight: 44, '& .MuiTab-root': { minHeight: 44, fontSize: 13, fontWeight: 700 } }}
        >
          <Tab value={CONSUMPTION_TABS.CONSUMPTIONS} label={RETURN_TEXTS.tabConsumptions} />
          {permissions.canCheckExternalOrderStock && (
            <Tab value={CONSUMPTION_TABS.RETURNS} label={RETURN_TEXTS.tabReturns} />
          )}
        </Tabs>
      </Box>
      {activeTab === CONSUMPTION_TABS.RETURNS ? (
        <>
          <ConsumptionReturnsPanel {...returnsPanelProps} />
          <ConsumptionReturnDrawer {...returnDrawerProps} />
        </>
      ) : (
        <>
          <ConsumptionsFilters
            filters={filters}
            warehouses={warehouses}
            onFilterChange={handleFilterChange}
            onSelectChange={handleFilterChange}
            scopeOptions={scopeOptions}
          />

          <ConsumptionsDataTable
            showCosts={permissions.canViewMaterialDashboard}
            data={data}
            isLoading={isLoading}
            sort={sort}
            onSort={handleSort}
            actionsDisabled={detailActionPending || printMutation.isPending}
            onView={handleViewDetail}
            onPrint={handlePrintConsumption}
          />

          {totalPages > 1 && (
            <ConsumptionsPagination
              totalPages={totalPages}
              totalCount={totalCount}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          )}

          <ConsumptionDetailDialog
            open={detailDialogOpen}
            onClose={handleCloseDetailDialog}
            consumptionId={selectedConsumptionId}
            onRefreshList={handleRefreshList}
            onPendingChange={setDetailActionPending}
          />

          <Snackbar
            open={printFeedback.open}
            autoHideDuration={6000}
            onClose={() => setPrintFeedback((prev) => ({ ...prev, open: false }))}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert
              severity={printFeedback.severity}
              onClose={() => setPrintFeedback((prev) => ({ ...prev, open: false }))}
              sx={{ width: '100%' }}
            >
              {printFeedback.message}
            </Alert>
          </Snackbar>
        </>
      )}
    </Box>
  );
};

export default ConsumptionsTable;
